import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { Toast } from 'react-bootstrap';
import { useStore } from '@nanostores/react';
import { SBChatToast, SBChatChannel, SBChatShow } from '../stores/SBChatStore';

// Styles as constants for better maintainability
const TOAST_CONTAINER_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: '30px',
  right: '30px',
  zIndex: 9999,
};

const TOAST_STYLE: React.CSSProperties = {
  borderRadius: '15px',
  boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
  border: '1px solid #e0e0e0',
  fontFamily: '"Nunito Sans", Helvetica, sans-serif',
  minWidth: '350px',
  maxWidth: '400px',
  width: '100%',
  overflow: 'hidden'
};

const TOAST_HEADER_STYLE: React.CSSProperties = {
  backgroundColor: '#0c6aca',
  color: '#fff',
  borderTopLeftRadius: '15px',
  borderTopRightRadius: '15px',
  padding: '12px 20px'
};

const TOAST_TITLE_STYLE: React.CSSProperties = {
  color: '#fff', 
  fontWeight: '600',
  fontSize: '16px',
  marginRight: '15px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '220px'
};

const TOAST_TIME_STYLE: React.CSSProperties = {
  color: '#fff', 
  opacity: '0.8',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const UNREAD_DOT_STYLE: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#ff0000',
  display: 'inline-block',
  animation: 'pulse 1s infinite'
};

const TOAST_BODY_STYLE: React.CSSProperties = {
  padding: '20px',
  fontSize: '16px',
  lineHeight: '1.5',
  fontWeight: '400',
  backgroundColor: '#fcfcfc',
  wordBreak: 'break-word' as 'break-word',
  borderBottomLeftRadius: '15px',
  borderBottomRightRadius: '15px'
};

// Consolidated function to open chat with optional channel
const openChat = (channelUrl = '') => {
  return new Promise((resolve, reject) => {
    try {
      // Set channel URL if provided
      if (channelUrl) {
        SBChatChannel.set(channelUrl);
      }
      
      // Open the chat window
      setTimeout(() => {
        SBChatShow.set(true);
        resolve(true);
      }, 50);
    } catch (error) {
      console.error('[SBChatToast] Error opening chat:', error);
      reject(error);
    }
  });
};

const SBChatToastNotification = () => {
  const toast = useStore(SBChatToast);
  const [show, setShow] = useState(false);
  const chatWindowOpen = useStore(SBChatShow);
  const currentChannel = useStore(SBChatChannel);
  const [isProcessingClick, setIsProcessingClick] = useState(false);
  
  // Combined effect for handling toast visibility
  useEffect(() => {
    // Handle new toast appearing
    if (toast.show) {
      setShow(true);
      
      // Auto-hide after 15 seconds
      const timer = setTimeout(() => {
        setShow(false);
        if (toast.show) {
          SBChatToast.set({...toast, show: false});
        }
      }, 15000);
      
      return () => clearTimeout(timer);
    } else {
      setShow(false);
    }
    
    // Close toast when chat window is opened
    if (chatWindowOpen && show) {
      setShow(false);
      if (toast.show) {
        SBChatToast.set({...toast, show: false});
      }
    }
  }, [toast, chatWindowOpen, show]);

  // Close button handler - just closes the toast without opening chat
  const handleClose = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    setShow(false);
    
    if (toast.show) {
      SBChatToast.set({...toast, show: false});
    }
  }, [toast]);
  
  // Click handler for the entire toast - closes toast and opens chat
  const handleClick = useCallback((e) => {
    if (isProcessingClick) return;
    
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    setIsProcessingClick(true);
    
    // First hide the toast UI
    setShow(false);
    
    // Make a local copy of channelUrl
    const channelUrl = toast.channelUrl;
    
    // Clear toast data
    if (toast.show) {
      SBChatToast.set({show: false, message: "", sender: "", channelUrl: ""});
    }
    
    // Open chat with appropriate channel
    const channelToUse = channelUrl || currentChannel || '';
    openChat(channelToUse)
      .catch(error => console.error('[SBChatToast] Failed to open chat:', error))
      .finally(() => setIsProcessingClick(false));
      
  }, [toast, isProcessingClick, currentChannel]);

  return (
    <div style={TOAST_CONTAINER_STYLE}>
      <div 
        onClick={handleClick}
        style={{ 
          cursor: isProcessingClick ? 'wait' : 'pointer',
          opacity: isProcessingClick ? 0.7 : 1
        }}
      >
        <Toast 
          show={show} 
          onClose={handleClose}
          style={TOAST_STYLE}
        >
          <Toast.Header 
            closeButton 
            style={TOAST_HEADER_STYLE}
          >
            <strong 
              className="me-auto" 
              style={TOAST_TITLE_STYLE}
            >
              {toast.sender}
            </strong>
            <small style={TOAST_TIME_STYLE}>
              <span style={UNREAD_DOT_STYLE}></span>
              just now
            </small>
          </Toast.Header>
          <Toast.Body style={TOAST_BODY_STYLE}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </div>
    </div>
  );
};

export default SBChatToastNotification;

// Add keyframes to the head
const animationStyle = document.createElement('style');
animationStyle.innerHTML = `
@keyframes pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0; transform: scale(1.2); }
  100% { opacity: 1; transform: scale(1); }
}`;
document.head.appendChild(animationStyle);